import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import PasswordInput from '../components/PasswordInput.jsx';
import Modal from '../components/Modal.jsx';

const ROLE_LABEL = { ADMIN: 'Administrador', DESIGNER: 'Designer', OPERATOR: 'Operador' };
const roleBadge = { ADMIN: 'bg-secondary-container text-on-secondary-container', DESIGNER: 'bg-tertiary-container text-on-tertiary-container', OPERATOR: 'bg-surface-variant text-on-surface-variant' };

export default function Users({ user }) {
  const isAdmin = user?.role === 'ADMIN';
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'OPERATOR' });
  const [error, setError] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');

  useEffect(() => {
    api.listUsers().then(setUsers).catch((e) => setError(e.message));
  }, []);

  async function create(e) {
    e.preventDefault();
    setCreateError('');
    setCreating(true);
    try {
      await api.createUser(form);
      setForm({ name: '', email: '', password: '', role: 'OPERATOR' });
      setIsCreateOpen(false);
      setUsers(await api.listUsers());
    } catch (err) {
      setCreateError(err.message || 'Erro ao criar usuário');
    } finally {
      setCreating(false);
    }
  }

  async function toggleStatus(u) {
    await api.updateUser(u.id, { status: u.status === 'ACTIVE' ? 'DISABLED' : 'ACTIVE' });
    setUsers(await api.listUsers());
  }

  async function changeRole(u, role) {
    await api.updateUser(u.id, { role });
    setUsers(await api.listUsers());
  }

  return (
    <div className="flex flex-col gap-base">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="studio-uniform-title font-h2 text-h2 text-on-surface">Gestão de Usuários</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-2">
            Gerencie acessos, permissões e monitore a atividade da equipe no Studio.
          </p>
        </div>
        {isAdmin && (
          <button
            onClick={() => {
              setCreateError('');
              setIsCreateOpen(true);
            }}
            className="bg-primary hover:bg-surface-tint text-on-primary font-label-sm text-label-sm px-6 py-3 rounded-lg flex items-center gap-2 transition-all shadow-md shadow-primary/20 cursor-pointer"
          >
            <span className="material-symbols-outlined text-[16px]">person_add</span>
            Novo Usuário
          </button>
        )}
      </div>

      {error && <p className="text-[11px] text-error">{error}</p>}

      <div className="bg-surface rounded-2xl shadow-sm overflow-hidden flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-surface-container-lowest text-on-surface-variant font-label-sm text-label-sm uppercase tracking-wider">
                <th className="px-6 py-4 font-semibold">Usuário</th>
                <th className="px-6 py-4 font-semibold">Perfil</th>
                <th className="px-6 py-4 font-semibold">Status</th>
                <th className="px-6 py-4 font-semibold text-right">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-container-highest font-body-md text-body-md text-on-surface">
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-surface-container-low transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-primary-container text-on-primary-container flex items-center justify-center font-bold text-[14px]">
                        {u.name?.slice(0, 2).toUpperCase() || u.email?.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold">{u.name}</span>
                        <span className="text-on-surface-variant text-[11px]">{u.email}</span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {isAdmin ? (
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        className="bg-surface rounded-lg px-3 py-1.5 border border-surface-container-high focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer text-[12px]"
                      >
                        {Object.entries(ROLE_LABEL).map(([v, l]) => (
                          <option key={v} value={v}>
                            {l}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <span className={`inline-flex items-center px-3 py-1 rounded-full font-label-sm text-label-sm text-[10px] ${roleBadge[u.role]}`}>
                        {ROLE_LABEL[u.role]}
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${
                      u.status === 'ACTIVE'
                        ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                        : 'bg-error-container/30 text-error border border-error/20'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${u.status === 'ACTIVE' ? 'bg-emerald-500' : 'bg-error'}`} />
                      {u.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {isAdmin && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => toggleStatus(u)}
                          title={u.status === 'ACTIVE' ? 'Desativar' : 'Reativar'}
                          className={`p-2 rounded-lg transition-colors cursor-pointer ${
                            u.status === 'ACTIVE'
                              ? 'text-on-surface-variant hover:text-error hover:bg-error-container/20'
                              : 'text-on-surface-variant hover:text-primary hover:bg-primary-container/20'
                          }`}
                        >
                          <span className="material-symbols-outlined text-[16px]">
                            {u.status === 'ACTIVE' ? 'block' : 'settings_backup_restore'}
                          </span>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-8 text-center text-on-surface-variant text-[12px]">
                    Nenhum usuário cadastrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="bg-surface-container-low px-6 py-4 flex items-center justify-between mt-auto">
          <span className="font-label-sm text-label-sm text-on-surface-variant">
            {users.length} usuário{users.length === 1 ? '' : 's'} no tenant
          </span>
          <button
            onClick={() => api.listUsers().then(setUsers)}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-on-surface-variant hover:bg-surface-container-high transition-colors cursor-pointer"
            title="Atualizar"
          >
            <span className="material-symbols-outlined text-[14px]">refresh</span>
          </button>
        </div>
      </div>

      <Modal
        open={isCreateOpen}
        onClose={() => !creating && setIsCreateOpen(false)}
        eyebrow="Acesso e Segurança"
        title="Novo Usuário"
        maxWidth="max-w-lg"
      >
        <form onSubmit={create} className="space-y-4">
          {createError && (
            <div className="rounded-xl border border-error/20 bg-error-container/30 p-3 text-[11px] text-error">
              {createError}
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-[11px] font-medium text-on-surface-variant mb-1">Nome Completo</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: João da Silva"
                required
                className="w-full bg-surface-low py-2.5 px-3 rounded-lg text-[12px] text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-surface-variant"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-on-surface-variant mb-1">E-mail de Acesso</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="usuario@empresa.com"
                required
                className="w-full bg-surface-low py-2.5 px-3 rounded-lg text-[12px] text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-surface-variant"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-on-surface-variant mb-1">Senha Provisória</label>
              <PasswordInput
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="Mínimo 8 caracteres"
                required
                className="w-full bg-surface-low py-2.5 px-3 pr-10 rounded-lg text-[12px] text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/50 border border-surface-variant"
              />
            </div>

            <div>
              <label className="block text-[11px] font-medium text-on-surface-variant mb-1">Perfil de Permissão</label>
              <select
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full bg-surface-low py-2.5 px-3 rounded-lg text-[12px] text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/50 border border-surface-variant"
              >
                {Object.entries(ROLE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>
                    {l}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-surface-container-high">
            <button
              type="button"
              onClick={() => setIsCreateOpen(false)}
              disabled={creating}
              className="rounded-xl px-4 py-2 text-[11px] font-medium text-on-surface-variant hover:bg-surface-container-high transition-colors disabled:opacity-50 cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={creating}
              className="rounded-xl bg-primary px-4 py-2 text-[11px] font-medium text-on-primary hover:bg-primary-fixed-dim transition-colors disabled:opacity-50 flex items-center gap-2 cursor-pointer"
            >
              {creating ? 'Criando…' : 'Criar Usuário'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}


